import { useSingletonAsync } from "./useSingletonAsync";

import { API_BASE } from '../config';

export interface PropertySpec {
  depends_on: any;
  key: string;
  label: string;
  control: string;
  value_type?: string;
  options?: OptionItem[];
  options_source?: string;
  default?: any;
  tooltip?: string;
}

export interface FormatterTypeSpec {
  id: string;
  label: string;
  category?: string;
  strategy_grammars?: string[];
  properties: PropertySpec[];
  description?: string;
  time_profile?: {
    kind?: string;
    resolution?: string;
    preferred_combine_on?: string;
    label?: string;
  };
}

export interface OptionItem {
  label: string;
  value: any;
}

const optionsCache = new Map<string, OptionItem[]>();
const optionsPromiseCache = new Map<string, Promise<OptionItem[]>>();

export function useFormatterTypes() {
  const { data, loading, error } = useSingletonAsync<FormatterTypeSpec[]>(
    "formatter_types",
    async () => {
      const res = await fetch(`${API_BASE}/formatter_types`);
      if (!res.ok) throw new Error(`GET /formatter_types → HTTP ${res.status}`);
      const json = await res.json();
      return json.formatter_types;
    }
  );

  function getFormatterTypeById(id: string): FormatterTypeSpec | undefined {
    return data?.find((dt) => dt.id === id);
  }

  async function getOptions(source: string): Promise<OptionItem[]> {
    const cached = optionsCache.get(source);
    if (cached) return cached;

    const existingPromise = optionsPromiseCache.get(source);
    if (existingPromise) return existingPromise;

    const fetchPromise = (async () => {
      try {
        const res = await fetch(`${API_BASE}/options/${encodeURIComponent(source)}`);
        if (!res.ok) throw new Error(`GET /options/${source} → HTTP ${res.status}`);
        const json = await res.json();
        const list = normalizeOptions(json?.options ?? []);
        optionsCache.set(source, list);
        return list;
      } finally {
        optionsPromiseCache.delete(source);
      }
    })();

    optionsPromiseCache.set(source, fetchPromise);
    return fetchPromise;
  }

  return {
    formatterTypes: data ?? [],
    loading,
    error,
    getFormatterTypeById,
    getOptions,
  };
}

export function getOptionsFromCache(source: string): OptionItem[] | undefined {
  return optionsCache.get(source);
}

function normalizeOptions(list: any[]): OptionItem[] {
  return list.map((o) => ({
    label: o.label ?? String(o.value),
    value: o.value,
  }));
}

export function coerceForSpec(spec: any, rawValue: any): any {
  if (spec.value_type === "int") {
    return parseInt(rawValue, 10);
  }
  if (spec.value_type === "float") {
    return parseFloat(rawValue);
  }
  if (spec.value_type === "boolean") {
    return Boolean(rawValue);
  }
  return rawValue;
}


import { singletonCache } from './useSingletonAsync';

export async function preloadFormatterTypes() {
  if (!singletonCache["formatter_types"]) {
    const res = await fetch(`${API_BASE}/formatter_types`);
    if (!res.ok) throw new Error(`GET /formatter_types → HTTP ${res.status}`);
    const json = await res.json();

    const data = json.formatter_types;

    singletonCache["formatter_types"] = {
      promise: Promise.resolve(data),
      data,
      error: null,
    };
  }
  return singletonCache["formatter_types"].data;
}

export function getDefaultFormatterTypeStatic(): string {
  return singletonCache["formatter_types"]?.data?.[0]?.id ?? '';
}
